import React, { useEffect } from 'react';
import { Box, Typography, Button, Container, Paper, Grid, TextField, CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserProfile, fetchUserProfile } from '../modules/user';
import { selectAccessToken } from '../modules/login';
import apiCall from '../utils/api';
import SendIcon from '@mui/icons-material/Send';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { AppDispatch } from '../store';
import AlertPopup from '../components/common/AlertPopup';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const schema = yup.object().shape({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  organization_name: yup.string(),
  message: yup.string().required('Message is required').min(10, 'Message must be at least 10 characters'),
});

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userProfile = useSelector(selectUserProfile);
  const accessToken = useSelector(selectAccessToken);
  const [loading, setLoading] = React.useState(false);
  const [alert, setAlert] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { register, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    values: {
      first_name: userProfile?.data?.first_name || '',
      last_name: userProfile?.data?.last_name || '',
      email: userProfile?.data?.email || '',
      phone: userProfile?.data?.phone_number || '',
      organization_name: userProfile?.data?.organization_name || '',
      message: ''
    }
  });

  const handleCloseAlert = () => setAlert({ ...alert, open: false });

  // Fetch profile if user is logged in but profile data is missing
  useEffect(() => {
    if (accessToken && !userProfile.data && !userProfile.loading) {
      dispatch(fetchUserProfile());
    }
  }, [accessToken, userProfile.data, userProfile.loading, dispatch]);



  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      await apiCall('POST', '/api/superadmin/contact/', data);
      setAlert({ open: true, message: 'Your message has been sent successfully!', severity: 'success' });
      
      if (accessToken && userProfile?.data) {
        // Keep profile info but clear message
        setValue('message', '');
      } else {
        // Clear everything for non-logged in users
        reset({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          organization_name: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setAlert({ open: true, message: 'Failed to send message. Please try again later.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: 8,
        background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mb: 4,
            color: '#666',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: '#000', backgroundColor: 'transparent' }
          }}
        >
          Back to Previous
        </Button>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ color: '#000' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 2,
                  borderRadius: 4,
                  backgroundColor: '#fff',
                  boxShadow: '0 10px 30px rgba(94, 142, 126, 0.1)',
                  color: '#5E8E7E',
                  mb: 3,
                }}
              >
                <ContactSupportIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, letterSpacing: '-0.02em', color: '#000', fontSize: { xs: '2.5rem', md: '3.2rem' } }}>
                Get in Touch
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.25rem', color: '#666', mb: 5, lineHeight: 1.6 }}>
                Have questions or need assistance? Fill out the form and our team will reach out to you as soon as possible.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#5E8E7E', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>
                    Email Us
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>support@talenthub.com</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#5E8E7E', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>
                    Call Us
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>+1 (555) 123-4567</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
              }}
            >
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      {...register('first_name')}
                      error={!!errors.first_name}
                      helperText={errors.first_name?.message as string}
                      InputProps={{ sx: { borderRadius: 4, backgroundColor: '#fff' } }}
                      InputLabelProps={{ shrink: watch('first_name') ? true : undefined }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      variant="outlined"
                      {...register('last_name')}
                      error={!!errors.last_name}
                      helperText={errors.last_name?.message as string}
                      InputProps={{ sx: { borderRadius: 4, backgroundColor: '#fff' } }}
                      InputLabelProps={{ shrink: watch('last_name') ? true : undefined }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      variant="outlined"
                      {...register('email')}
                      error={!!errors.email}
                      helperText={errors.email?.message as string}
                      InputProps={{ sx: { borderRadius: 4, backgroundColor: '#fff' } }}
                      InputLabelProps={{ shrink: watch('email') ? true : undefined }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      variant="outlined"
                      {...register('phone')}
                      error={!!errors.phone}
                      helperText={errors.phone?.message as string}
                      InputProps={{ sx: { borderRadius: 4, backgroundColor: '#fff' } }}
                      InputLabelProps={{ shrink: watch('phone') ? true : undefined }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Organization"
                      variant="outlined"
                      {...register('organization_name')}
                      disabled={!!userProfile?.data?.organization_name}
                      InputProps={{ sx: { borderRadius: 4, backgroundColor: '#fff' } }}
                      InputLabelProps={{ shrink: watch('organization_name') ? true : undefined }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Your Message"
                      variant="outlined"
                      multiline
                      rows={5}
                      {...register('message')}
                      error={!!errors.message}
                      helperText={errors.message?.message as string}
                      InputProps={{ sx: { borderRadius: 4, backgroundColor: '#fff' } }}
                      InputLabelProps={{ shrink: watch('message') ? true : undefined }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{
                        mt: 1,
                        py: 2,
                        borderRadius: 4,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        backgroundColor: '#5E8E7E',
                        boxShadow: '0 10px 25px rgba(94, 142, 126, 0.4)',
                        '&:hover': {
                          backgroundColor: '#4a7164',
                          boxShadow: '0 12px 30px rgba(94, 142, 126, 0.5)',
                        },
                      }}
                    >
                      {loading ? 'Sending Message...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <AlertPopup 
        open={alert.open} 
        message={alert.message} 
        severity={alert.severity} 
        onClose={handleCloseAlert} 
      />
    </Box>
  );
};

export default ContactPage;
